import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Container, EmptyList, FlexColumn, FlexColumnOverflow, SmallInfo } from "../Order/OrderTable/OrderTable.styled"
import TableRow from "../Ui/Table/TableRow"
import TableBlurLoading from "../Ui/Table/TableBlurLoading"
import ErrorMessage from "../Ui/Messages/ErrorMessage"
import { ReactComponent as CartIcon } from "../../images/icons/cart-shopping.svg"
import { ReactComponent as MagnifyingGlassIcon } from "../../images/icons/magnifying-glass.svg"
import { StyledTableHeader, ProductsTableContainer } from "./TopProducts.styled"
import { ViewOrdersButton } from "./ViewOrdersButton.styled"
import { bucket } from "../../views/Dashboards/DashboardSalesAggregations"
import { ProductImage, ProductImageContainer } from "../Products/Shared.styled"
import placeholder from "../../images/placeholder.svg"
import { useDispatch, useSelector } from "react-redux"
import { selectTopProductsFilteredBy, selectTopProductsFilteredByType } from "lib/store/services/analytics/selectors"
import { setTopProductsExportData } from "lib/store/services/analytics/slice"
import { setProductNavigation } from "lib/store/services/Order/orderSlice"
import { filterByAll, filterByDiscount, filterByParent, filterByVariant } from "./AnalyticsData"
import Dinero from "dinero.js"
import { useLazyQuery } from "@apollo/client"
import GET_PRODUCT_VARIANT_STOCK from "../../graphql/queries/product/GetProductVariantStock"
import { Tooltip } from "../Ui/Tooltip"
import FlagIcon from "../Ui/FlagIcon"
import { ProductTotalSales } from "./TopProductsDetails"
type Props = {
  productParentNames?: { [productId: string]: string }
  topProducts: bucket[]
  loading: boolean
  error: object | undefined
  currencyCode: string
  ratesMap: Record<string, Record<string, number>>
  startDate: string
  endDate: string
}

export const TopProducts = (
  {
    topProducts,
    loading,
    error,
    productParentNames,
    ratesMap,
    currencyCode,
    startDate,
    endDate
  }: Props) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const filterByType = useSelector(selectTopProductsFilteredByType)
  const filterBy = useSelector(selectTopProductsFilteredBy)
  const [productVariantStocks, setProductVariantStocks] = useState<{ [variantId: string]: string }>({})
  const [stocksLoading, setStocksLoading] = useState(false)
  const [stocksError, setStocksError] = useState<Error | null>(null)

  function getProductParentName(parentId: string | undefined) {
    if (!parentId || !productParentNames) {
      return parentId
    }
    return productParentNames[parentId] ?? parentId
  }

  // Dispatch export data to Redux when data changes
  useEffect(() => {
    if (topProducts.length > 0) {
      dispatch(setTopProductsExportData({
        topProducts,
        productVariantStocks,
        currencyCode,
        ratesMap
      }))
    }
  }, [topProducts, productVariantStocks, currencyCode, ratesMap, dispatch])

  const [getProductStock] = useLazyQuery(GET_PRODUCT_VARIANT_STOCK, {
    context: {
      skipRumLogging: true,
    },
    onError: (error) => {
      console.error("Error fetching product stock:", error)
      setStocksError(error)
    }
  })

  useEffect(() => {
    async function fetchStocks() {
      if (!topProducts?.length || filterByType === filterByParent) return

      setStocksLoading(true)
      setStocksError(null)

      try {
        const newStocks: { [variantId: string]: string } = {}

        await Promise.all(
          topProducts.map(async (product) => {
            const id = product.topProductDetails?.hits.hits[0]._source.productVariantId
            if (!id) return

            try {
              const result = await getProductStock({
                variables: { id }
              })

              const stockQty = result?.data?.Product?.variant?.Stock?.stockQuantity
              if (stockQty !== undefined) {
                newStocks[id] = stockQty
              }
            } catch (err) {
              console.error(`Error fetching stock for product ${id}:`, err)
            }
          })
        )
        setProductVariantStocks(newStocks)
      } catch (err) {
        console.error("Error fetching stocks:", err)
        setStocksError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setStocksLoading(false)
      }
    }

    fetchStocks()
  }, [topProducts, getProductStock])

  function toAverageDiscountPercentage(d: bucket): string {
    // Average discount in percentage can be calculated with a fixed currency
    const price = Dinero({ amount: d.priceTotal?.value })
    const discount = Dinero({ amount: d.discountTotal?.value })

    const totalBeforeDiscount = price.add(discount)

    if (discount.isZero()) {
      return `0%`;
    }

    const discountPercentage = discount.multiply(100).divide(totalBeforeDiscount.getAmount())

    return `~${Math.round(discountPercentage.getAmount())}%`
  }

  const viewOrdersWithProduct = (productId: string) => {
    dispatch(setProductNavigation({
      productId,
      fromDate: startDate ? new Date(startDate).toISOString().slice(0, 16) : "",
      toDate: endDate ? new Date(endDate).toISOString().slice(0, 16) : ""
    }))
    navigate("/orders")
  }

  const tableHeader = (
    <StyledTableHeader>
      <div></div>
      <div>Product Name</div>
      <div className="hideOnMediumScreens">ParentId</div>
      {filterByType === filterByVariant ? <div className="hideOnMediumScreens">VariantId</div> : <></>}
      <div>Total Quantity</div>
      <div className="hideOnSmallScreens">Total Sales</div>
      <div className="hideOnMediumScreens">Top Market</div>
      <div className="hideOnMediumScreens">Top Store</div>
      {filterBy === filterByDiscount ? (
        <div className="hideOnSmallScreens">Campaign</div>
      ) : (
        <></>
      )}
      {filterBy === filterByAll || filterBy === filterByDiscount ? (
        <div className="hideOnSmallScreens compactColumn">Average Discount</div>
      ) : (
        <></>
      )}
      {filterByType === filterByVariant ? <div className="hideOnSmallScreens">Stock</div> : <></>}
      <div></div>
    </StyledTableHeader>
  )

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.src = placeholder
  }

  const toTooltipContent = (filterCount: number, totalCount: number, name?: string) => {
    const percentage = Math.round((filterCount / totalCount) * 100);
    if (percentage === 100) {
      return `${filterCount} orders (100%)`;
    }

    if (name) {
      return `${name}: ${filterCount} orders (~${percentage}%)`;
    }
    return `${filterCount} orders (~${percentage}%)`;
  };

  const getRows = (product?: bucket, index?: number) =>
    product ? (
      <TableRow
        key={product.topProductDetails?.hits.hits[0]._source.productVariantId}
        handleOnClick={() => {
          navigate(`/products/${product?.topProductDetails?.hits.hits[0]._source.productParentId}`)
        }}
      >
        <FlexColumn>
          <p>#{(index ?? 0) + 1}</p>
        </FlexColumn>
        <ProductImageContainer>
          <ProductImage
            src={product.topProductDetails?.hits.hits[0]._source.imageUrl as string || undefined}
            onError={handleImageError}
          />
        </ProductImageContainer>
        <FlexColumnOverflow>
          <Tooltip id="tooltip2" />
          <SmallInfo
            className={"hideOnSmallScreens"}
            data-tooltip-id="tooltip2"
            data-tooltip-content={
              filterByParent === filterByType
                ? getProductParentName(
                  product.topProductDetails?.hits.hits[0]._source.productParentId
                )
                : product.topProductDetails?.hits.hits[0]._source.name            }
            data-tooltip-delay-hide={100}>
            <p>
              {filterByParent === filterByType
                ? getProductParentName(
                    product.topProductDetails?.hits.hits[0]._source.productParentId
                  )
                : product.topProductDetails?.hits.hits[0]._source.name}
            </p>
          </SmallInfo>
        </FlexColumnOverflow>
        <FlexColumn className="hideOnMediumScreens">
          <p>{product.topProductDetails?.hits.hits[0]._source.productParentId}</p>
        </FlexColumn>
        {filterByType === filterByVariant ? (
          <FlexColumn className="hideOnMediumScreens">
            <p>{product.topProductDetails?.hits.hits[0]._source.productVariantId}</p>
          </FlexColumn>
        ) : (
          <></>
        )}
        <FlexColumn>
          <p>{product.totalQuantity.value.toLocaleString()}</p>
        </FlexColumn>
        <FlexColumn className="hideOnSmallScreens">
          <p>{currencyCode} <ProductTotalSales product={product} currencyCode={currencyCode} ratesMap={ratesMap} /></p>
        </FlexColumn>
        <FlexColumn className="hideOnMediumScreens">
          <Tooltip id="tooltip" />
          {product.ordersTopValues?.topCountries.buckets[0].doc_count && (
            <SmallInfo
              className={"hideOnSmallScreens"}
              data-tooltip-id="tooltip"
              data-tooltip-content={toTooltipContent(
                product.ordersTopValues?.topCountries.buckets[0].doc_count,
                product.totalQuantity.value
              )}
              data-tooltip-delay-hide={100}
            >
              <FlagIcon countryCode={product.ordersTopValues?.topCountries.buckets[0].key} />
            </SmallInfo>
          )}
        </FlexColumn>
        <FlexColumnOverflow className="hideOnMediumScreens">
          <Tooltip id="tooltip2" />
          {product.ordersTopValues?.topStores.buckets[0].doc_count && (
            <SmallInfo
              className={"hideOnSmallScreens"}
              data-tooltip-id="tooltip2"
              data-tooltip-content={toTooltipContent(
                product.ordersTopValues?.topStores.buckets[0].doc_count,
                product.totalQuantity.value,
                product.ordersTopValues?.topStores.buckets[0].key
              )}
              data-tooltip-delay-hide={100}
            >
              <p>{product.ordersTopValues?.topStores.buckets[0].key}</p>
            </SmallInfo>
          )}
        </FlexColumnOverflow>
        {filterBy === filterByDiscount ? (
          <FlexColumnOverflow className="hideOnSmallScreens">
            <Tooltip id="tooltip2" />
            <SmallInfo
              className={"hideOnSmallScreens"}
              data-tooltip-id="tooltip2"
              data-tooltip-content={
                product.topProductDetails?.hits.hits[0]._source.campaign?.name
              }
              data-tooltip-delay-hide={100}>
            <p>{(product.topProductDetails?.hits.hits[0]._source.campaign?.name) || "N/A"}</p>
            </SmallInfo>
          </FlexColumnOverflow>
        ) : (
          <></>
        )}
        {filterBy === filterByAll || filterBy === filterByDiscount ? (
          <FlexColumn className="hideOnSmallScreens compactColumn">
            <p>{toAverageDiscountPercentage(product)}</p>
          </FlexColumn>
        ) : (
          <></>
        )}
        {filterByType === filterByVariant ? (
          <FlexColumn className="hideOnSmallScreens">
            <p>
              {stocksLoading
                ? "Loading..."
                : product.topProductDetails?.hits.hits[0]._source.productVariantId
                  ? productVariantStocks[product.topProductDetails?.hits.hits[0]._source.productVariantId]?.toLocaleString()
                  : "N/A"}
            </p>
          </FlexColumn>
        ) : (
          <></>
        )}
        <FlexColumn>
          <ViewOrdersButton
            onClick={(e) => {
              e.stopPropagation()
              const productId = filterByType === filterByParent
                ? product.topProductDetails?.hits.hits[0]._source.productParentId
                : product.topProductDetails?.hits.hits[0]._source.productVariantId
              if (productId) viewOrdersWithProduct(productId)
            }}
          >
            <MagnifyingGlassIcon />
            View orders
          </ViewOrdersButton>
        </FlexColumn>
      </TableRow>
    ) : (
      <></>
    )

  if (loading || (stocksLoading && Object.keys(productVariantStocks).length === 0))
    return <TableBlurLoading numberOfRows={4} tableHeader={tableHeader} rows={getRows(undefined, 0)} />

  return (
    <Container>
      {(error || stocksError) && (
        <ErrorMessage
          showRefreshButton
          message={
            <>
              {error ? "Error loading Top Products." : "Error loading product stock data."} Try refreshing the page, or
              contact <a href="mailto:support@brinkcommerce.com">support</a>.
            </>
          }
        />
      )}
      {topProducts && topProducts.length > 0 ? (
        <>
          {tableHeader}
          <ProductsTableContainer>
            {topProducts.map((order, index) => getRows(order, index))}
          </ProductsTableContainer>
        </>
      ) : (
        <EmptyList>
          <CartIcon />
          <p> No orders found</p>
        </EmptyList>
      )}
    </Container>
  )
}

